import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Form, Modal } from 'react-bootstrap';
import 'rc-slider/assets/index.css';
import { faEye, faEyeSlash, faCopy, faMagicWandSparkles, faPen } from '@fortawesome/free-solid-svg-icons';
import SaveButton from '../Buttons/SaveButton';
import DiscardButton from '../Buttons/DiscardButton';
import { Utils } from '../../Services/Utils';
import { EntityColors } from '../../Themes/EntityColors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import InputGroup from 'react-bootstrap/InputGroup';
import generator from 'generate-password-browser';
import WebsitePicker from '../WebsitePicker';
import { AdminUserCreateRequest, WebsitesData } from '../../Models/Models';
import { WebsiteService } from '../../Services/WebsiteService';
import { Constants } from '../../Models/Constants';
import { UserService,  } from '../../Services/UserService';
import { toAdminUserForm } from '../../Mappers/UserMapper';
import ToggleSwitch from '../ToggleSwitch';
import CustomTooltip from '../Tooltips/CustomTooltip';
import { ToastContext, UserContext } from '../../Contexts';
import FormShell from '../Form/FormShell';
import RequiredLabel from "../Form/RequiredLabel";
import CharCount from '../Form/CharCount';
import { FieldLengths } from '../../Models/FieldLengths';
import { useIsDirty } from '../../Hooks/useIsDirty';

const UserCreateEditComponent = ({ isEditMode }: { isEditMode: boolean }) => {
    const { currentUser }: any = useContext(UserContext);

    useEffect(() => {
        Utils.setDocumentTitle(isEditMode ? 'Úprava používateľa' : 'Tvorba používateľa');
    }, [isEditMode]);

    const { action, icon } = EntityColors.user;
    const { getWebsites } = WebsiteService();
    const { createUser, getUserById, updateUser } = UserService();
    const { userId } = useParams<{ userId: string }>();
    const navigate = useNavigate();

    const [adminUser, setAdminUser] = useState<AdminUserCreateRequest>(Constants.defaultAdminUserCreateRequest);
    const [showPassword, setShowPassword] = useState(false);
    const [websites, setWebsites] = useState<WebsitesData[] | null>(null);
    const [copied, setCopied] = useState(false);
    const [generated, setGenerated] = useState(false);
    const [newPasswordInEdit, setNewPasswordInEdit] = useState(false);
    const [showSuperAdminConfirm, setShowSuperAdminConfirm] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const isDirty = useIsDirty(adminUser, loaded);

    const nonce = useMemo(() => Math.random().toString(36).slice(2), []);

    const requestToggleSuperAdmin = (next: boolean) => {
        if (next && !adminUser.isSuperAdmin) {
            setShowSuperAdminConfirm(true);
            return;
        }
        setAdminUser(prev => ({ ...prev, isSuperAdmin: next }));
    };

    const confirmSuperAdmin = () => {
        setShowSuperAdminConfirm(false);
        setAdminUser(prev => ({ ...prev, isSuperAdmin: true }));
    };

    const cancelSuperAdmin = () => {
        setShowSuperAdminConfirm(false);
    };

    const generatePassword = () => {
        setGenerated(true);
        setTimeout(() => setGenerated(false), 750);
        const pwd = generator.generate({ length: 16, numbers: true, lowercase: true, uppercase: true });
        setShowPassword(true);
        setAdminUser(prev => ({ ...prev, password: pwd }));
    };

    const load = async () => {
        const response = await getWebsites();
        setWebsites(response.data);
        if (isEditMode) {
            setAdminUser(toAdminUserForm(await getUserById(Number(userId))));
        }
        setLoaded(true);
    };

    useEffect(() => {
        if (!currentUser.isSuperAdmin) {
            navigate('/404');
            return;
        }
        void load();
    }, []);

    const { setToast }: any = useContext(ToastContext);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (newPasswordInEdit && adminUser.password.length < 8) {
            setToast({ message: 'Heslo musí obsahovať aspoň 8 znakov!' });
            return;
        }
        if (isEditMode) await updateUser({ id: Number(userId), ...adminUser });
        else await createUser(adminUser);
    };

    if (!currentUser.isSuperAdmin) return <></>;

    return (
        <>
            <FormShell
                title={isEditMode ? 'Upraviť používateľa' : 'Vytvoriť používateľa'}
                icon={icon}
                accentColor={action}
                backHref="/user/table"
                isDirty={isDirty}
            >
                <form onSubmit={handleSubmit} autoComplete="off" className="user-select-none">
                    <Form.Group className="mb-3">
                        <div className="st-field-head">
                            <RequiredLabel>Meno</RequiredLabel>
                            <CharCount value={adminUser.displayName} max={FieldLengths.adminUserDisplayName} />
                        </div>
                        <Form.Control
                            type="text"
                            placeholder="Meno a priezvisko"
                            required
                            maxLength={FieldLengths.adminUserDisplayName}
                            value={adminUser.displayName}
                            onChange={e => setAdminUser(prev => ({ ...prev, displayName: e.target.value }))}
                            autoComplete="off"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <div className="st-field-head">
                            <RequiredLabel>Email</RequiredLabel>
                            <CharCount value={adminUser.email} max={FieldLengths.adminUserEmail} />
                        </div>
                        <Form.Control
                            type="email"
                            placeholder="example@email.com"
                            required
                            maxLength={FieldLengths.adminUserEmail}
                            value={adminUser.email}
                            disabled={isEditMode}
                            onChange={e => setAdminUser(prev => ({ ...prev, email: e.target.value }))}
                            autoComplete="off"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <RequiredLabel>Heslo</RequiredLabel>
                        {(!isEditMode || newPasswordInEdit) ? (
                            <>
                                <InputGroup>
                                    <CustomTooltip placement="left" message={showPassword ? 'Skryť' : 'Zobraziť'}>
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => { if (!adminUser.isPasswordTemporary) setShowPassword(p => !p); }}
                                            disabled={adminUser.isPasswordTemporary}
                                        >
                                            <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                        </Button>
                                    </CustomTooltip>
                                    <Form.Control
                                        disabled={adminUser.isPasswordTemporary}
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="password"
                                        required
                                        value={adminUser.password}
                                        onChange={e => setAdminUser(prev => ({ ...prev, password: e.target.value }))}
                                        name={`newUserPassword_${nonce}`}
                                        autoComplete="new-password"
                                        className={copied ? 'st-input-flash-success' : (generated ? 'st-input-flash-warn' : undefined)}
                                    />
                                    <CustomTooltip placement="top" message="Skopírovať">
                                        <Button
                                            variant={copied ? 'success' : 'outline-secondary'}
                                            onClick={() => {
                                                navigator.clipboard.writeText(adminUser.password);
                                                setCopied(true);
                                                setTimeout(() => setCopied(false), 750);
                                            }}
                                            disabled={!adminUser.password}
                                        >
                                            <FontAwesomeIcon icon={faCopy} />
                                        </Button>
                                    </CustomTooltip>
                                    <CustomTooltip placement="top" message="Vygenerovať">
                                        <Button
                                            variant={generated ? 'warning' : 'outline-secondary'}
                                            onClick={generatePassword}
                                        >
                                            <FontAwesomeIcon icon={faMagicWandSparkles} />
                                        </Button>
                                    </CustomTooltip>
                                </InputGroup>
                                <p className="text-muted m-0">Minimálne 8 znakov</p>
                                <Form.Group className="mt-2">
                                    <ToggleSwitch
                                        label="Dočasné heslo"
                                        checked={adminUser.isPasswordTemporary}
                                        onChange={(next) => {
                                            setAdminUser(prev => ({ ...prev, isPasswordTemporary: next }));
                                            if (next) generatePassword();
                                        }}
                                        accentColor={action}
                                        id="tmpPass"
                                    />
                                </Form.Group>
                            </>
                        ) : (
                            <InputGroup>
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => {
                                        setNewPasswordInEdit(true);
                                        if (adminUser.isPasswordTemporary) generatePassword();
                                    }}
                                >
                                    <FontAwesomeIcon icon={faPen} />
                                </Button>
                            </InputGroup>
                        )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Weby</Form.Label>
                        <div className="st-accent-box" style={{ '--box-accent': action } as React.CSSProperties}>
                            {websites != null ? (
                                <WebsitePicker
                                    items={websites}
                                    value={adminUser.websiteIds}
                                    onChange={(ids: number[]) => setAdminUser(prev => ({ ...prev, websiteIds: ids }))}
                                    accentColor={action}
                                    maxHeight={300}
                                />
                            ) : (
                                <p className="mb-0 text-secondary">Načítavam...</p>
                            )}
                        </div>
                    </Form.Group>

                    <Form.Group className="mt-2 mb-3">
                        <ToggleSwitch
                            label="SuperAdmin:"
                            disabled={adminUser.email === currentUser.email && adminUser.isSuperAdmin}
                            checked={adminUser.isSuperAdmin}
                            onChange={requestToggleSuperAdmin}
                            accentColor={action}
                            id="superAdmin"
                        />
                    </Form.Group>

                    <div className="d-flex justify-content-center mt-3 gap-3">
                        <DiscardButton to="/user/table" isDirty={isDirty} />
                        <SaveButton type="submit" />
                    </div>
                </form>
            </FormShell>

            <Modal show={showSuperAdminConfirm} className="user-select-none" centered backdrop="static">
                <Modal.Header>
                    <Modal.Title>Pozor!</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Týmto používateľovi udelíš oprávnenie <b>SuperAdmin</b>.
                    Získa plný prístup ku všetkým funkciám a webom. Chceš pokračovať?
                </Modal.Body>
                <Modal.Footer>
                    <Button type="button" variant="outline-secondary" onClick={cancelSuperAdmin}>Zrušiť</Button>
                    <button
                        type="button"
                        className="st-btn st-btn--fill"
                        style={{ '--btn-accent': action } as React.CSSProperties}
                        onClick={confirmSuperAdmin}
                    >
                        Áno, udeliť prístup
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default UserCreateEditComponent;
